import React, { useState, useMemo, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Settings, DollarSign } from 'lucide-react';

import { cn } from '@/lib/utils';
import { EASE_EXPO_OUT } from '@/lib/motion';

// UI Components
import { Label, Card, Button, Badge, Switch } from '@/components/ui';
import { ToggleSection } from '@/components/form/ToggleSection';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';

// Form Components
import { HolidayMultiSelect } from './HolidayMultiSelect';
import { CustomizedFieldIndicator } from '../../form/CustomizedFieldIndicator';
import { HolidayPremiumMethodSelector } from './components/HolidayPremiumMethodSelector';
import { ApplicableRolesRow } from '../shared/AdvancedSettingsSection';
import { ConfigSection } from '../shared/ConfigSection';
import { hasAnyHalfDayRoles } from '../shared/utils';
import { SurchargeMethodPanel } from '../shared/surcharge/SurchargeMethodPanel';
import { useMethodSwapAnimation } from '../shared/surcharge/useMethodSwapAnimation';
import { OffsetConfigSection } from '../shared/OffsetConfigSection';
import {
  buildHolidayOffsetConfig,
  handleHolidayOffsetChange,
} from './utils/buildHolidayOffsetConfig';
import { buildPreviewLadder } from '../shared/buildPreviewLadder';
import { FixedDollarSection } from './sections/FixedDollarSection';
import { HourlyPremiumMethodSection } from './sections/HourlyPremiumMethodSection';

import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';

// Types & Utils
import type { HolidayConfigFormProps } from './types';
import { calculateHolidayMarkdownVariables } from './utils/markdownVariables';
import { useOvertimeLadderInfo } from '@/hooks/useOvertimeLadderInfo';

// Extend the interface to include customizedFields
interface ExtendedHolidayConfigFormProps extends HolidayConfigFormProps {
  customizedFields?: Record<string, boolean>;
  hideToggle?: boolean;
}

const HolidayConfigFormComponent: React.FC<ExtendedHolidayConfigFormProps> = ({
  title,
  description,
  holidaySelection,
  applyHolidayPremium,
  onApplyHolidayPremiumChange,
  holidayPremiumType,
  onHolidayPremiumTypeChange,
  holidayOffsetAmount,
  onHolidayOffsetAmountChange,
  holidayApplyPremiumAfterOvertime,
  onHolidayApplyPremiumAfterOvertimeChange,
  holidayApplyOnHalfDays,
  onHolidayApplyOnHalfDaysChange,
  holidayApplicableRoleIds,
  onHolidayApplicableRoleIdsChange,
  holidayMultiplierFloorCode,
  holidayMultiplierFloorCustomValue,
  onHolidayMultiplierFloorCodeChange,
  onHolidayMultiplierFloorCustomValueChange,
  holidayFixedPremiumAmount,
  holidayHourlyPremiumType,
  onHolidayHourlyPremiumTypeChange,
  onHolidayUpliftTypeChange,
  holidayUpliftPercentage,
  holidayUpliftAmount,
  roles,
  rateMultipliers,
  isCustomized,
  onRevert,
  fixedSurchargeAmountProps,
  fixedSurchargeAmountError,
  holidayUpliftPercentageProps,
  holidayUpliftPercentageError,
  holidayUpliftAmountProps,
  holidayUpliftAmountError,
  holidayMultiplierError,
  holidaySelectionError,
  noCard,
  customizedFields = {},
  context = 'user',
  hideToggle,
  extendedOvertimeEnabled,
  onExtendedOvertimeEnabledChange,
  extendedOvertimeTiers,
  onExtendedOvertimeTiersChange,
  defaultExtendedTiers,
}) => {
  const anyHalfDayRoles = useMemo(() => hasAnyHalfDayRoles(roles ?? [], holidayApplicableRoleIds), [roles, holidayApplicableRoleIds]);
  const multiSelectContainerRef = useRef<HTMLDivElement>(null);
  const [holidaySelectionOpen, setHolidaySelectionOpen] = useState(true);
  const [surchargeConfigOpen, setSurchargeConfigOpen] = useState(true);
  const [holidaySheetOpen, setHolidaySheetOpen] = useState(false);

  const {
    containerStyle: methodContainerStyle,
    containerRef: methodContentContainerRef,
    activePanelRef: activeMethodPanelRef,
    fadeDuration: methodFadeDuration,
  } = useMethodSwapAnimation(holidayPremiumType);

  const allNonStraightTimeMultipliers = useMemo(() =>
    rateMultipliers.filter(m => m.code !== 'STRAIGHT_TIME'),
  [rateMultipliers]);

  const { highestOvertimeRate, highestOvertimeTierName, maxExtendedTiers } = useOvertimeLadderInfo(rateMultipliers);

  const previewLadder = useMemo(() => buildPreviewLadder(rateMultipliers), [rateMultipliers]);

  const markdownVariables = useMemo(() =>
    calculateHolidayMarkdownVariables(roles, rateMultipliers, holidayHourlyPremiumType, holidayUpliftPercentage, holidayUpliftAmount, holidayApplyPremiumAfterOvertime, holidayOffsetAmount ?? 1, holidayMultiplierFloorCode, holidayFixedPremiumAmount, extendedOvertimeEnabled, extendedOvertimeTiers, holidayMultiplierFloorCustomValue),
    [rateMultipliers, roles, holidayHourlyPremiumType, holidayUpliftPercentage, holidayUpliftAmount, holidayApplyPremiumAfterOvertime, holidayOffsetAmount, holidayMultiplierFloorCode, holidayFixedPremiumAmount, extendedOvertimeEnabled, extendedOvertimeTiers, holidayMultiplierFloorCustomValue]
  );

  const formFields = (
    <div className="space-y-3">
      <ConfigSection
        title="Trigger Conditions"
        description="Configure which holiday shifts qualify for premium rates."
        icon={Settings}

        isOpen={holidaySelectionOpen}
        onOpenChange={setHolidaySelectionOpen}
      >
        <div>
          <div className="space-y-3">
            <div>
              <div className="flex items-center gap-2">
                <Label className={fieldLabelClass}>Holiday Selection</Label>
                <CustomizedFieldIndicator isCustomized={customizedFields.holidays} context={context} />
                <button
                  type="button"
                  className="touch-target-pseudo md:hidden ml-auto text-[11px] text-primary uppercase tracking-wide font-medium hover:underline"
                  onClick={() => setHolidaySheetOpen(true)}
                >
                  Configure
                </button>
              </div>
              <p className={cn(fieldDescriptionClass, "mt-0 md:mt-1")}>
                Choose which holidays qualify for premium rates.
              </p>
            </div>
            <div ref={multiSelectContainerRef} data-validation-key="holidays">
              <HolidayMultiSelect
                selectedCatalogIds={holidaySelection.selectedCatalogIds}
                selectedCustomIds={holidaySelection.selectedCustomIds}
                onCatalogChange={holidaySelection.onSelectedCatalogIdsChange}
                onCustomChange={holidaySelection.onSelectedCustomIdsChange}
                sheetOpen={holidaySheetOpen}
                onSheetOpenChange={setHolidaySheetOpen}
              />
            </div>
            {holidaySelectionError}
          </div>

          <ApplicableRolesRow
            className="space-y-5"
            roles={roles || []}
            applicableRoleIds={holidayApplicableRoleIds ?? null}
            onApplicableRoleIdsChange={onHolidayApplicableRoleIdsChange || (() => {})}
            isCustomized={customizedFields.holiday_applicable_role_ids}
            context={context}
            featureName="holiday-premium"
            showHalfDayOption={anyHalfDayRoles}
            applyOnHalfDays={holidayApplyOnHalfDays}
            onApplyOnHalfDaysChange={onHolidayApplyOnHalfDaysChange}
            halfDayDescription="Apply this premium even when the shift is billed as a half day."
          />
        </div>
      </ConfigSection>

      <ConfigSection
        title="Premium Configuration"
        description="Configure how the holiday premium is calculated."
        icon={DollarSign}

        isOpen={surchargeConfigOpen}
        onOpenChange={setSurchargeConfigOpen}
      >
        <div>
          <HolidayPremiumMethodSelector
            holidayPremiumType={holidayPremiumType}
            onHolidayPremiumTypeChange={onHolidayPremiumTypeChange}
            allNonStraightTimeMultipliers={allNonStraightTimeMultipliers}
            customizedFields={customizedFields}
            context={context}
            markdownVariables={markdownVariables}
          />

          <div
            ref={methodContentContainerRef}
            className="overflow-hidden"
            style={methodContainerStyle}
          >
            <div className="relative grid">
              <AnimatePresence mode="sync" initial={false}>
                {holidayPremiumType === 'offset_overtime' && (
                  <SurchargeMethodPanel
                    key="offset_overtime"
                    activePanelRef={activeMethodPanelRef}
                    transitionDuration={methodFadeDuration}
                  >
                    <OffsetConfigSection
                      domain="holiday"
                      method={holidayPremiumType as 'offset_overtime' | 'guaranteed_rate'}
                      config={buildHolidayOffsetConfig(
                        holidayPremiumType as 'offset_overtime' | 'guaranteed_rate',
                        {
                          holidayOffsetAmount,
                          holidayMultiplierFloorCode,
                          holidayMultiplierFloorCustomValue,
                        },
                      )}
                      onChange={(next) =>
                        handleHolidayOffsetChange(
                          holidayPremiumType as 'offset_overtime' | 'guaranteed_rate',
                          next,
                          {
                            holidayOffsetAmount,
                            holidayMultiplierFloorCode,
                            holidayMultiplierFloorCustomValue,
                          },
                          {
                            onHolidayOffsetAmountChange,
                            onHolidayMultiplierFloorCodeChange,
                            onHolidayMultiplierFloorCustomValueChange,
                          },
                        )
                      }
                      rateMultipliers={previewLadder}
                      tierPickerMultipliers={allNonStraightTimeMultipliers}
                      tierPickerMultiplierFilter={(m) => m.code !== 'STRAIGHT_TIME'}
                      context={context === 'client' ? 'client' : 'user'}
                      customizedFields={{
                        floorTier: customizedFields?.['holiday_floor_tier_code'],
                        overtimeOffset: customizedFields?.['holiday_overtime_offset'],
                      }}
                      floorTierErrorElement={holidayMultiplierError}
                      extendedOvertimeEnabled={extendedOvertimeEnabled ?? false}
                      extendedOvertimeTiers={extendedOvertimeTiers ?? []}
                      extendedOvertime={{
                        enabled: extendedOvertimeEnabled ?? false,
                        onEnabledChange: onExtendedOvertimeEnabledChange ?? (() => {}),
                        tiers: extendedOvertimeTiers ?? [],
                        onTiersChange: onExtendedOvertimeTiersChange ?? (() => {}),
                        highestOvertimeRate,
                        highestOvertimeTierName,
                        defaultTiers: defaultExtendedTiers,
                        maxTiers: maxExtendedTiers,
                      }}
                    />
                  </SurchargeMethodPanel>
                )}

                {holidayPremiumType === 'guaranteed_rate' && (
                  <SurchargeMethodPanel
                    key="guaranteed_rate"
                    activePanelRef={activeMethodPanelRef}
                    transitionDuration={methodFadeDuration}
                  >
                    <OffsetConfigSection
                      domain="holiday"
                      method={holidayPremiumType as 'offset_overtime' | 'guaranteed_rate'}
                      config={buildHolidayOffsetConfig(
                        holidayPremiumType as 'offset_overtime' | 'guaranteed_rate',
                        {
                          holidayOffsetAmount,
                          holidayMultiplierFloorCode,
                          holidayMultiplierFloorCustomValue,
                        },
                      )}
                      onChange={(next) =>
                        handleHolidayOffsetChange(
                          holidayPremiumType as 'offset_overtime' | 'guaranteed_rate',
                          next,
                          {
                            holidayOffsetAmount,
                            holidayMultiplierFloorCode,
                            holidayMultiplierFloorCustomValue,
                          },
                          {
                            onHolidayOffsetAmountChange,
                            onHolidayMultiplierFloorCodeChange,
                            onHolidayMultiplierFloorCustomValueChange,
                          },
                        )
                      }
                      rateMultipliers={previewLadder}
                      tierPickerMultipliers={allNonStraightTimeMultipliers}
                      tierPickerMultiplierFilter={(m) => m.code !== 'STRAIGHT_TIME'}
                      context={context === 'client' ? 'client' : 'user'}
                      customizedFields={{
                        floorTier: customizedFields?.['holiday_floor_tier_code'],
                        overtimeOffset: customizedFields?.['holiday_overtime_offset'],
                      }}
                      floorTierErrorElement={holidayMultiplierError}
                      extendedOvertimeEnabled={extendedOvertimeEnabled ?? false}
                      extendedOvertimeTiers={extendedOvertimeTiers ?? []}
                      extendedOvertime={{
                        enabled: extendedOvertimeEnabled ?? false,
                        onEnabledChange: onExtendedOvertimeEnabledChange ?? (() => {}),
                        tiers: extendedOvertimeTiers ?? [],
                        onTiersChange: onExtendedOvertimeTiersChange ?? (() => {}),
                        highestOvertimeRate,
                        highestOvertimeTierName,
                        defaultTiers: defaultExtendedTiers,
                        maxTiers: maxExtendedTiers,
                      }}
                    />
                  </SurchargeMethodPanel>
                )}

                {holidayPremiumType === 'fixed_dollar' && (
                  <SurchargeMethodPanel
                    key="fixed_dollar"
                    activePanelRef={activeMethodPanelRef}
                    transitionDuration={methodFadeDuration}
                  >
                    <FixedDollarSection
                      fixedSurchargeAmountProps={fixedSurchargeAmountProps}
                      fixedSurchargeAmountError={fixedSurchargeAmountError}
                      customizedFields={customizedFields}
                      context={context}
                    />
                  </SurchargeMethodPanel>
                )}

                {holidayPremiumType === 'hourly_premium' && (
                  <SurchargeMethodPanel
                    key="hourly_premium"
                    activePanelRef={activeMethodPanelRef}
                    transitionDuration={methodFadeDuration}
                  >
                    <HourlyPremiumMethodSection
                      holidayHourlyPremiumType={holidayHourlyPremiumType}
                      onHolidayHourlyPremiumTypeChange={onHolidayHourlyPremiumTypeChange}
                      holidayPremiumBasis={holidayApplyPremiumAfterOvertime ? 'ot_inclusive' : 'base_rate'}
                      onHolidayPremiumBasisChange={(basis) => onHolidayApplyPremiumAfterOvertimeChange?.(basis === 'ot_inclusive')}
                      holidayPremiumValueProps={holidayUpliftPercentageProps ?? holidayUpliftAmountProps}
                      holidayPremiumValueError={holidayUpliftPercentageError ?? holidayUpliftAmountError}
                      customizedFields={customizedFields}
                      context={context}
                    />
                  </SurchargeMethodPanel>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </ConfigSection>
    </div>
  );

  if (hideToggle) {
    return formFields;
  }

  const content = (
    <div className="space-y-3 md:space-y-6">
        {(title || description) && (
          <div className="space-y-1 text-center">
            {title && (
              <div className="flex items-center justify-center gap-3">
                <h3 className="font-heading uppercase tracking-wide text-lg text-foreground">{title}</h3>
                {isCustomized && <Badge variant="default">{context === 'client' ? 'Client' : 'Profile'} Override</Badge>}
              </div>
            )}
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
        )}
        <ToggleSection
          id="applyHolidayPremium"
          label={
            <div className="flex items-center gap-1.5">
              <span>Holiday Premium</span>
              <CustomizedFieldIndicator isCustomized={customizedFields.apply_holiday_premium} context={context} />
            </div>
          }
          description="Enable special rates for work performed on selected holidays."
          checked={applyHolidayPremium}
          onCheckedChange={onApplyHolidayPremiumChange}
          variant="default"
        >
          {formFields}
        </ToggleSection>

        <AnimatedCollapse open={!!(isCustomized && onRevert)}>
              <div className="pt-4 border-t flex justify-center">
                <Button variant="link" className="p-0 h-auto text-muted-foreground" onClick={onRevert}>
                  Revert to Profile Defaults
                </Button>
              </div>
        </AnimatedCollapse>
    </div>
  );

  if (noCard) {
    return content;
  }

  return (
    <Card
      className={cn(
        "animate-fade-in rounded equipment-panel p-3 pb-4 md:p-8",
        "max-w-3xl mx-auto"
      )}
    >
      {content}
    </Card>
  );
};

export const HolidayConfigForm = React.memo(HolidayConfigFormComponent);
HolidayConfigForm.displayName = 'HolidayConfigForm';
